import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Settings() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [cp, setCp] = useState({ current: "", next: "", confirm: "" });
  const [del, setDel] = useState({ password: "", confirmText: "" });
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const changePw = async (e) => {
    e.preventDefault();
    if (cp.next !== cp.confirm) { toast.error("Le password non coincidono"); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: cp.current, new_password: cp.next });
      toast.success("Password aggiornata");
      setCp({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally { setLoading(false); }
  };

  const deleteAccount = async (e) => {
    e.preventDefault();
    if (del.confirmText !== "CANCELLA") { toast.error("Scrivi CANCELLA per confermare"); return; }
    if (!window.confirm("Cancellazione definitiva: account + tutti gli annunci. Procedere?")) return;
    try {
      await api.delete("/users/me", { data: { password: del.password } });
      toast.success("Account cancellato");
      logout();
      nav("/");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    }
  };

  const resendVerify = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Email di verifica inviata");
    } catch (e) { toast.error("Errore"); }
    finally { setResending(false); }
  };

  if (!user) return null;

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-12" data-testid="settings-page">
      <div className="text-xs uppercase tracking-[0.3em] text-[#FFBE00] mb-2">· Impostazioni</div>
      <h1 className="font-heading font-black text-4xl sm:text-5xl leading-tight mb-10">Il tuo account.</h1>

      <section className="hm-card p-6 mb-6">
        <div className="text-xs uppercase tracking-widest text-[#FFBE00] mb-4">· Profilo</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div><div className="text-zinc-500 text-xs uppercase tracking-wider">Nome</div><div className="text-white">{user.name}</div></div>
          <div><div className="text-zinc-500 text-xs uppercase tracking-wider">Email</div><div className="text-white flex items-center gap-2">{user.email}{user.email_verified ? <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-[#22C55E]/20 text-[#22C55E]">Verificata</span> : <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-zinc-800 text-zinc-400">Non verificata</span>}</div></div>
          <div><div className="text-zinc-500 text-xs uppercase tracking-wider">Ruolo</div><div className="text-white">{user.role}</div></div>
          <div><div className="text-zinc-500 text-xs uppercase tracking-wider">Abbonamento</div><div className="text-white">{user.subscription ? `${user.subscription.tier.toUpperCase()} · scade ${new Date(user.subscription.expires_at).toLocaleDateString("it-IT")}` : "Nessuno"}</div></div>
        </div>
        {!user.email_verified && (
          <button onClick={resendVerify} disabled={resending} className="mt-5 px-4 py-2 rounded-md border border-[#E619A8] text-[#E619A8] text-xs uppercase tracking-wider hover:bg-[#E619A8] hover:text-white transition-all disabled:opacity-50" data-testid="resend-verify-btn">
            {resending ? "Invio…" : "Reinvia email di verifica"}
          </button>
        )}
      </section>

      <section className="hm-card p-6 mb-6">
        <div className="text-xs uppercase tracking-widest text-[#E619A8] mb-4">· Cambia password</div>
        <form onSubmit={changePw} className="space-y-3 max-w-md" data-testid="change-pw-form">
          <input type="password" required placeholder="Password attuale" value={cp.current} onChange={(e) => setCp({ ...cp, current: e.target.value })} className="w-full bg-[#0A0A0A] border border-zinc-800 rounded-md px-4 py-2.5 text-sm focus:border-[#E619A8] outline-none" data-testid="cp-current" />
          <input type="password" required minLength={6} placeholder="Nuova password (min 6)" value={cp.next} onChange={(e) => setCp({ ...cp, next: e.target.value })} className="w-full bg-[#0A0A0A] border border-zinc-800 rounded-md px-4 py-2.5 text-sm focus:border-[#E619A8] outline-none" data-testid="cp-next" />
          <input type="password" required minLength={6} placeholder="Conferma nuova" value={cp.confirm} onChange={(e) => setCp({ ...cp, confirm: e.target.value })} className="w-full bg-[#0A0A0A] border border-zinc-800 rounded-md px-4 py-2.5 text-sm focus:border-[#E619A8] outline-none" data-testid="cp-confirm" />
          <button type="submit" disabled={loading} className="px-5 py-2.5 rounded-md bg-[#FFBE00] text-black font-bold uppercase tracking-wider text-xs hover:bg-[#E619A8] hover:text-white transition-all disabled:opacity-50" data-testid="cp-submit">
            {loading ? "Salvataggio…" : "Cambia password"}
          </button>
        </form>
      </section>

      <section className="hm-card p-6 border-red-900/50">
        <div className="text-xs uppercase tracking-widest text-red-500 mb-4">· Zona pericolo</div>
        <p className="text-sm text-zinc-400 mb-4">La cancellazione dell'account è <strong className="text-red-400">definitiva</strong>. Verranno eliminati: il tuo profilo, tutti i tuoi annunci, le foto caricate, l'abbonamento attivo. Non è possibile annullare.</p>
        <form onSubmit={deleteAccount} className="space-y-3 max-w-md" data-testid="delete-account-form">
          <input type="password" required placeholder="Password attuale" value={del.password} onChange={(e) => setDel({ ...del, password: e.target.value })} className="w-full bg-[#0A0A0A] border border-red-900 rounded-md px-4 py-2.5 text-sm focus:border-red-500 outline-none" data-testid="del-password" />
          <input required placeholder="Scrivi CANCELLA per confermare" value={del.confirmText} onChange={(e) => setDel({ ...del, confirmText: e.target.value })} className="w-full bg-[#0A0A0A] border border-red-900 rounded-md px-4 py-2.5 text-sm focus:border-red-500 outline-none font-mono" data-testid="del-confirm" />
          <button type="submit" className="px-5 py-2.5 rounded-md bg-red-600 text-white font-bold uppercase tracking-wider text-xs hover:bg-red-500" data-testid="del-submit">
            Cancella definitivamente il mio account
          </button>
        </form>
      </section>
    </div>
  );
}
